// frontend/src/app/components/projects/project-corpus/status-badge.component.ts
import { CommonModule } from '@angular/common';
import { Component, Input, computed, signal } from '@angular/core';
import { ImportJobStatus, PaperItemStatus } from '../../../project-corpus/domain/types';

export type StatusBadgeVariant = ImportJobStatus | PaperItemStatus;

interface BadgePresentation {
  readonly label: string;
  readonly classes: string;
}

const VARIANT_MAP: Record<StatusBadgeVariant, BadgePresentation> = {
  // Import job statuses
  RUNNING: { label: 'Running', classes: 'bg-blue-100 text-blue-800' },
  COMPLETED: { label: 'Completed', classes: 'bg-green-100 text-green-800' },
  FAILED: { label: 'Failed', classes: 'bg-red-100 text-red-800' },
  UNDONE: { label: 'Undone', classes: 'bg-slate-100 text-slate-500 line-through' },
  // Paper item statuses
  COMPLETE: { label: 'Complete', classes: 'bg-green-100 text-green-800' },
  NEEDS_PDF: { label: 'Needs PDF', classes: 'bg-amber-100 text-amber-800' },
  NEEDS_DOI: { label: 'Needs DOI', classes: 'bg-amber-100 text-amber-800' },
  NEEDS_METADATA: { label: 'Needs metadata', classes: 'bg-amber-100 text-amber-800' },
  EXTRACTION_FAILED: { label: 'Extraction failed', classes: 'bg-red-100 text-red-800' },
  DUPLICATE_SKIPPED: { label: 'Duplicate', classes: 'bg-slate-100 text-slate-700' },
};

/**
 * Compact, accessible status badge reused by import-job cards and paper rows.
 * Maps each `ImportJobStatus` / `PaperItemStatus` variant to a Tailwind class
 * combination + a human-readable label.
 */
@Component({
  selector: 'app-status-badge',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './status-badge.component.html',
  styleUrls: ['./status-badge.component.scss'],
})
export class StatusBadgeComponent {
  private readonly _variant = signal<StatusBadgeVariant | null>(null);

  @Input({ required: true })
  set variant(value: StatusBadgeVariant) {
    this._variant.set(value);
  }
  get variant(): StatusBadgeVariant {
    return this._variant() ?? 'COMPLETE';
  }

  readonly presentation = computed<BadgePresentation>(() => {
    const v = this._variant();
    if (!v) return { label: '', classes: 'bg-slate-100 text-slate-700' };
    return VARIANT_MAP[v] ?? { label: v, classes: 'bg-slate-100 text-slate-700' };
  });

  readonly showSpinner = computed(() => this._variant() === 'RUNNING');
}
